import React, { useState } from 'react'
import '../styles/CosmeticsModal.css'

/* ─────────────────────────────────────────────
   Props:
     onClose()         — zamknij modal
     getAuthHeader()   — zwraca { Authorization: ... }
     onSaved()         — callback po zapisaniu (odśwież profil)
     showToast(msg)    — callback do toastów w rodzicu
   ───────────────────────────────────────────── */

export default function CosmeticsModal({ onClose, getAuthHeader, onSaved, showToast }) {
  const [tab, setTab]                   = useState('avatar')
  const [cosmetics, setCosmetics]       = useState(null)
  const [pendingAvatar, setPendingAvatar] = useState(null)
  const [pendingBorder, setPendingBorder] = useState(null)
  const [loading, setLoading]           = useState(true)
  const [saving, setSaving]             = useState(false)

  // Fetch on mount
  React.useEffect(() => {
    const fetchCosmetics = async () => {
      try {
        const headers = await getAuthHeader()
        const res = await fetch('/api/profile/cosmetics', { headers })
        if (res.ok) {
          const data = await res.json()
          setCosmetics(data)
          setPendingAvatar(data.equipped_avatar ? parseInt(data.equipped_avatar) : null)
          setPendingBorder(data.equipped_border ? parseInt(data.equipped_border) : null)
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    fetchCosmetics()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      const headers = await getAuthHeader()
      const equippedAvatar = cosmetics?.equipped_avatar ? parseInt(cosmetics.equipped_avatar) : null
      const equippedBorder = cosmetics?.equipped_border ? parseInt(cosmetics.equipped_border) : null

      if (pendingAvatar !== equippedAvatar && pendingAvatar !== null) {
        await fetch('/api/profile/equip', {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ cosmetic_id: pendingAvatar, type: 'avatar' })
        })
      }

      if (pendingBorder !== equippedBorder) {
        await fetch('/api/profile/equip', {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ cosmetic_id: pendingBorder ?? null, type: 'border' })
        })
      }

      await onSaved()
      showToast('Saved!')
      onClose()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="cm-overlay" onClick={onClose}>
      <div className="cm-modal" onClick={e => e.stopPropagation()}>

        <div className="cm-header">
          <h2 className="cm-title">Customize</h2>
          <button className="cm-close" onClick={onClose}>✕</button>
        </div>

        <div className="cm-tabs">
          <button
            className={`cm-tab ${tab === 'avatar' ? 'active' : ''}`}
            onClick={() => setTab('avatar')}
          >Avatar</button>
          <button
            className={`cm-tab ${tab === 'border' ? 'active' : ''}`}
            onClick={() => setTab('border')}
          >Border</button>
        </div>

        <div className="cm-body">
          {loading ? (
            <div className="cm-loading">Loading...</div>
          ) : tab === 'avatar' ? (
            <div className="cm-grid">
              {cosmetics.avatars.length === 0 && (
                <div className="cm-empty">No avatars unlocked yet.</div>
              )}
              {cosmetics.avatars.map(av => (
                <div
                  key={av.id}
                  className={`cm-item ${pendingAvatar === av.id ? 'selected' : ''}`}
                  onClick={() => setPendingAvatar(av.id)}
                  title={av.name}
                >
                  <img src={av.image_url} alt={av.name} />
                  {pendingAvatar === av.id && <div className="cm-check">✓</div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="cm-grid">
              <div
                className={`cm-item cm-item--none ${pendingBorder === null ? 'selected' : ''}`}
                onClick={() => setPendingBorder(null)}
              >
                <span className="cm-none-icon">✕</span>
                <div className="cm-none-label">None</div>
                {pendingBorder === null && <div className="cm-check">✓</div>}
              </div>
              {cosmetics.borders.length === 0 && (
                <div className="cm-empty">No borders unlocked yet.</div>
              )}
              {cosmetics.borders.map(br => (
                <div
                  key={br.id}
                  className={`cm-item ${pendingBorder === br.id ? 'selected' : ''}`}
                  onClick={() => setPendingBorder(br.id)}
                  title={br.name}
                >
                  <img src={br.image_url} alt={br.name} />
                  {pendingBorder === br.id && <div className="cm-check">✓</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cm-footer">
          <button className="cm-cancel" onClick={onClose}>Cancel</button>
          <button className="cm-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

      </div>
    </div>
  )
}